import { Box, Button, Grid, Group, Input, SegmentedControl, Select, Stack, Text, TextInput, Title, UnstyledButton } from "@mantine/core"
import { useForm } from "@mantine/form"
import { DatePickerInput } from "@mantine/dates"
import { yupResolver } from "mantine-form-yup-resolver"
import classes from "./form.module.css"
import { MapPinIcon, MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import useSWR from "swr"
import * as yup from "yup"
import { DisabledReason, FormChecklist, HelpButtonGroup, HelpIcon, i18n, nextStepToast, useChecklistMissing, useHelpContext } from "@dehub/theme"
import { NetworkApi, OrderApi } from "../../apis"
import { IBusiness } from '../../models/Business'
import { IGeneral } from '../../store/General'
import { taxNumber } from "../../utils"
import { dateFormat, dateTimeSecFormat } from "../../utils/date"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { renderName } from "../../utils/render-name"
import { PageLayout } from "../layout"
import { usePermission } from "../permission"
import { LabelField } from "../ui"
import { DotPill } from "../delivery/atoms"
import { orderStatusTone } from "../buyer-sales/order-status-pill"
import { SupplierSalesDynamicForm } from "./dynamic-form"
import { renderBusinessName } from "../../utils/render-business-name"

const schema = yup.object({
  regNumber: yup.string().matches(taxNumber, () => i18n.t("common:validation.invalidTaxId")).required(() => i18n.t("common:validation.required")).nullable(),
  partnerName: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
  supplierId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
  deliveryType: yup.string().required(() => i18n.t("common:validation.selectRequired")).nullable(),
  deliveryDate: yup.string().when(["deliveryType"], (deliveryType) => {
    if (typeof deliveryType === "string" && deliveryType === "CUSTOM_DATE") {
      return yup.string().required(() => i18n.t("common:validation.required")).nullable()
    }
    return yup.string().nullable()
  }),
  deliveryCheck: yup.string().when(["deliveryType"], (deliveryType) => {
    if (typeof deliveryType === "string" && deliveryType === "DEFAULT_DATE") {
      return yup.string().required(() => i18n.t("common:validation.required")).nullable()
    }
    return yup.string().nullable()
  }),
  lines: yup.array().of(
    yup.object({
      quantity: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
    }),
  ),
  additionalLines: yup.array().of(
    yup.object({
      name: yup.string().required(() => i18n.t("common:validation.required")).nullable().max(255, () => i18n.t("common:validation.tooLong")),
      unit: yup.string().required(() => i18n.t("common:validation.required")).nullable().max(100, () => i18n.t("common:validation.tooLong")),
      price: yup.number().min(1, () => i18n.t("common:validation.required")).required(() => i18n.t("common:validation.required")).nullable(),
      quantity: yup.number().min(1, () => i18n.t("common:validation.required")).required(() => i18n.t("common:validation.required")).nullable(),
      discountType: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
      discountValue: yup.number().min(1, () => i18n.t("common:validation.required")).required(() => i18n.t("common:validation.required")).typeError(""),
    }),
  ),
  attachments: yup.array().of(
    yup.object({
      name: yup.string().required(() => i18n.t("common:validation.required")).nullable().max(255, () => i18n.t("common:validation.tooLong")),
      url: yup.string().required(() => i18n.t("common:validation.required")).nullable().max(500, () => i18n.t("common:validation.tooLong")),
      description: yup.string().required(() => i18n.t("common:validation.required")).nullable().max(255, () => i18n.t("common:validation.tooLong")),
    }),
  ),
})

export type IFormData = {
  regNumber: string
  partnerName: string
  businessName: string
  businessId: string
  receiverBranchId: string
  deliveryType: string
  deliveryDate: string
  receiverStaffId: string
  lines: []
  additionalLines: []
  senderNote: string
  senderAdditionalNote: string
  shippingAmount: string
  discountType: string
  discountValue: string
  attachments: []
  toReview: boolean
  send: boolean
  supplierId: string
  types: boolean
  deliveryCheck: string
}

const initData = {
  regNumber: undefined,
  businessName: undefined,
  businessId: undefined,
  receiverBranchId: undefined,
  deliveryDate: undefined,
  receiverStaffId: undefined,
  lines: [],
  additionalLines: [],
  senderNote: undefined,
  senderAdditionalNote: undefined,
  shippingAmount: undefined,
  discountType: undefined,
  discountValue: undefined,
  attachments: [],
  toReview: false,
  send: false,
  supplierId: undefined,
  types: false,
  deliveryCheck: undefined,
}

/** Plain object the edit page sends to PUT /order/:id (order/update.ts). */
function buildPayload(value: any, order: any, businessData: any, send: boolean, deliveryDate: string) {
  const picked = value?.types
    ? { businessId: businessData?.id, receiverBranchId: businessData?.receiverBranch?.id, receiverStaffId: businessData?.receiverStaff?.id }
    : { businessId: order?.receiverBusinessId, receiverBranchId: order?.receiverBranchId, receiverStaffId: order?.receiverStaffId }
  return {
    ...picked,
    deliveryType: value?.deliveryType,
    deliveryDate,
    senderNote: value?.senderNote,
    senderAdditionalNote: value?.senderAdditionalNote,
    shippingAmount: value?.shippingAmount,
    discountType: value?.discountType,
    discountValue: value?.discountValue,
    toReview: false,
    send,
    attachments: (value?.attachments || []).map((item: any) => ({ description: item?.description, name: item?.name, url: item?.url })),
    lines: (value?.lines || []).map((item: any) => ({
      variantId: item?.variantId,
      parentVariantId: item?.variant?.id || item?.variantParentId || item?.variantId || item?.id,
      discountType: item?.discountType,
      discountValue: item?.discountValue,
      quantity: item?.quantity,
      warehouseId: item.warehouseId,
      price: item?.price,
    })),
    additionalLines: (value?.additionalLines || []).map((item: any) => ({
      name: item?.name,
      unit: item?.unit,
      price: item?.price,
      quantity: item?.quantity,
      discountType: item?.discountType,
      discountValue: item?.discountValue,
    })),
  }
}

export function SupplierSalesForm({ action }: { action: any[] }) {
  const { t } = useTranslation(["supplierSales", "common"])
  const breadcrumbs = useBreadcrumb()
  const navigate = useNavigate()
  const order = action[1]
  const orderStatus = useSelector((state: { general: IGeneral }) => state.general.orderStatus)
  const deliveryDateTypes = useSelector((state: { general: IGeneral }) => state.general.deliveryDateTypes)
  const [loading, setLoading] = useState(false)
  const [businessData, setBusinessData] = useState<any>("")

  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")

  // Save and send are different backend permissions (order/update.ts): ORD_SAVE or ORD_SEND saves,
  // ORD_SEND sends. Both buttons stay visible and say why they are unavailable.
  const saveReason = ORD_SAVE.hasAccess || ORD_SEND.hasAccess ? null : ORD_SAVE.reason
  const sendReason = ORD_SEND.hasAccess ? null : ORD_SEND.reason

  // @dehub/help: the form is the SENDER side; the checklist feeds the drawer card and the Send button's reason popover.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => { setHelpContext({ side: "SENDER", status: order?.orderStatus, vars: { code: order?.salesCode } }) }, [order?.orderStatus, order?.salesCode, setHelpContext])
  const checklistMissing = useChecklistMissing()

  // The saved delivery date seeds «deliveryCheck», so a scheduled order can be saved without re-picking the date (O60).
  const savedDeliveryDate: string | null = order?.deliveryDate ? new Date(order.deliveryDate).toISOString() : null

  const [data] = useState<IFormData>({
    deliveryDate: "",
    deliveryType: "",
    regNumber: "",
    businessName: "",
    lines: [],
    attachments: [],
    additionalLines: [],
    senderNote: "",
    senderAdditionalNote: "",
    discountType: "",
    discountValue: "",
    shippingAmount: "",
    deliveryCheck: savedDeliveryDate ?? "",
    ...(action && action[0] === "edit"
      ? {
        ...order,
        partnerName: order?.receiverBusiness?.profileName,
        regNumber: order?.receiverBusiness?.regNumber,
        supplierId: order?.receiverBusinessId,
        types: false,
      }
      : {}),
  })

  const form = useForm<IFormData>({
    mode: "controlled",
    initialValues: {
      ...initData,
      ...data,
      additionalLines: data?.additionalLines || [],
      lines: data?.lines || [],
      attachments: data?.attachments || [],
    },
    validate: yupResolver(schema),
  })

  const onSubmit = async (value: any, type: "save" | "send") => {
    let deliveryDateFormatted: string | null
    if (value?.deliveryType === "DEFAULT_DATE") {
      deliveryDateFormatted = value?.deliveryCheck ? dayjs(value.deliveryCheck).format("YYYY-MM-DD") : null
    } else if (value?.deliveryType === "CUSTOM_DATE") {
      deliveryDateFormatted = value?.deliveryDate ? dayjs(value.deliveryDate).format("YYYY-MM-DD") : null
    } else {
      deliveryDateFormatted = null
    }
    const fresh = !!value.types
    if (fresh ? !businessData?.supplierStaff : !order?.supplierStaff) return message.error(t("supplierSales:sales.setupSupplierManager"))
    if (fresh ? !businessData?.buyerStaff : !order?.buyerStaff) return message.error(t("supplierSales:sales.setupBuyerManager"))
    if (!value?.lines) return message.error(t("supplierSales:sales.addProductService"))
    if (fresh ? !businessData?.receiverStaff : !order?.receiverStaff?.id) return message.error(t("supplierSales:sales.noReceiverStaff"))
    if (fresh ? businessData?.receiverBranchId === undefined : value?.receiverBranchId === undefined) return message.error(t("supplierSales:sales.noBuyerBranch"))

    setLoading(true)
    try {
      await OrderApi.update(order?.id, buildPayload(value, order, businessData, type === "send", deliveryDateFormatted || ""))
      nextStepToast(type === "send" ? "order.supplier-sales-new.toast.send" : "order.supplier-sales-new.toast.save", {
        vars: { code: order?.salesCode },
        fallback: { title: t(type === "send" ? "supplierSales:sales.sendSuccess" : "supplierSales:sales.saveSuccess") },
      })
      navigate("/supplier-sales")
    } catch (err) {
      // Backend errors arrive as `error.<домэйн>.<нэр>` keys; message.error translates them.
      message.error((err as HttpHandler)?.message || "")
    } finally {
      setLoading(false)
    }
  }

  const { data: networkData } = useSWR(`/ord/network/select/partner-data`, async () => {
    if (ORD_SAVE.hasAccess || ORD_SEND.hasAccess) {
      const res = await NetworkApi.select({ regNumber: "", partnerName: "", limit: 1000 })
      const mapedRes = res.reduce((acc: any, it: any) => {
        acc[it.id] = it
        return acc
      }, {})
      return { res, mapedRes }
    }
  })

  const businessOnChange = async (id: any) => {
    if (id) {
      try {
        const res = await NetworkApi.get(id)
        setBusinessData(res)
        form.setFieldValue("types", true)
        form.setFieldValue("lines", undefined as any)
      } catch (error) {
        message.error((error as HttpHandler)?.message!)
      }
    }
  }

  // Nearest six delivery days. The saved date is added as a chip when it is not one of them.
  const dayMs = 24 * 60 * 60 * 1000
  const base = Date.now() - (Date.now() % (3 * dayMs))
  const upcoming = [176400000, 262800000, 349200000, 435600000, 522000000, 608400000].map((o) => new Date(base + o))
  const dayLabels = [t("common:days.sun"), t("common:days.mon"), t("common:days.tue"), t("common:days.wed"), t("common:days.thu"), t("common:days.fri"), t("common:days.sat")]
  const sameDay = (a: Date, b: Date) => dayjs(a).format("YYYY-MM-DD") === dayjs(b).format("YYYY-MM-DD")
  const saved = savedDeliveryDate ? new Date(savedDeliveryDate) : null
  const dayChips = [...(saved && !upcoming.some((d) => sameDay(d, saved)) ? [saved] : []), ...upcoming]
  const selectedChip = form.values.deliveryCheck ? new Date(form.values.deliveryCheck) : null

  // Customer fields: from the newly picked business, otherwise from the saved order.
  const info = form.values?.types
    ? { partner: businessData?.partner, business: businessData, paymentTerm: businessData?.paymentTerm, buyerStaff: businessData?.buyerStaff, supplierStaff: businessData?.supplierStaff, receiverStaff: businessData?.receiverStaff, receiverBranch: businessData?.receiverBranch }
    : { partner: order?.receiverBusiness?.partner, business: order?.receiverBusiness, paymentTerm: order?.paymentTerm, buyerStaff: order?.buyerStaff, supplierStaff: order?.supplierStaff, receiverStaff: order?.receiverStaff, receiverBranch: order?.receiverBranch }
  const refAndName = (b: any, partner = false) => {
    const code = b?.refCode
    const name = partner ? b?.businessName : b?.profileName
    return code || name ? [code, name].filter(Boolean).join(" ") : "-"
  }

  const statusObj = orderStatus.find((c: any) => c.code === order?.orderStatus)

  // «Илгээхэд дутуу» checklist (spec 10 §2.9): ids match the seed's checklist items; targets are data-help-id spots.
  const v = form.values
  const dayDone = v.deliveryType === "DEFAULT_DATE" ? !!v.deliveryCheck : v.deliveryType === "CUSTOM_DATE" ? !!v.deliveryDate : false
  const feeDone = v.shippingAmount !== "" && v.shippingAmount !== null && v.shippingAmount !== undefined
  const checklistItems = useMemo(() => [
    { id: "cust", done: !!v.supplierId, target: "cust-box" },
    { id: "items", done: (v.lines?.length ?? 0) > 0, target: "items-card" },
    { id: "day", done: dayDone, target: "days" },
    { id: "fee", done: feeDone, target: "fee" },
  ], [v.supplierId, v.lines?.length, dayDone, feeDone])

  return (
    <PageLayout
      title={
        <Group gap="sm" wrap="nowrap">
          <Text span inherit>{t("supplierSales:sales.editOrder")}</Text>
          {/* Dot pill like the canvas «● Түр төлөв»; the reference list colour for DRAFT is near-white. */}
          <DotPill tone={orderStatusTone(order?.orderStatus)}>{statusObj?.sentName ?? statusObj?.name ?? "-"}</DotPill>
        </Group>
      }
      subTitle={t("supplierSales:sales.editOrderSubTitle")}
      breadcrumb={breadcrumbs}
      extra={[
        <Button onClick={() => navigate("/supplier-sales")} key="back" variant="default">
          {t("common:actions.back")}
        </Button>,
        <DisabledReason key="save" helpKey="order.supplier-sales-new.reason.save" reason="PERM" when={!!saveReason}>
          <HelpButtonGroup helpKey="order.supplier-sales-new.action.save">
            <Button
              data-help-id="action-save"
              variant="default"
              loading={loading}
              title={saveReason ?? undefined}
              onClick={() => {
                const { hasErrors } = form.validate()
                if (!hasErrors) onSubmit(form.values, "save")
              }}>
              {t("common:actions.save")}
            </Button>
          </HelpButtonGroup>
        </DisabledReason>,
        // Send: permission reason, or the missing-items list with «Харуулах» spotlight (R4 / spec 10 §2.9)
        <DisabledReason key="send" helpKey="order.supplier-sales-new.reason.send" reason={sendReason ? "PERM" : undefined} when={!!sendReason || checklistMissing.length > 0} missing={sendReason ? undefined : checklistMissing}>
          <HelpButtonGroup helpKey="order.supplier-sales-new.action.send">
            <Button
              data-help-id="action-send"
              loading={loading}
              title={sendReason ?? undefined}
              onClick={() => {
                const { hasErrors } = form.validate()
                if (!hasErrors) onSubmit(form.values, "send")
              }}>
              {t("common:actions.send")}
            </Button>
          </HelpButtonGroup>
        </DisabledReason>,
      ]}>
      {/* Who registered the order: the order's own author, not the signed-in user (O61). */}
      <Group gap="xl" my="sm">
        <FormChecklist helpKey="order.supplier-sales-new.checklist.main" items={checklistItems} />
        <Text fz={14}>
          {t("supplierSales:sales.registeredBy")}: <Text span fw={650} fz={14}>{renderName(order?.orderedUser) || "-"}</Text>
        </Text>
        <Text fz={14}>
          {t("common:fields.date")}: <Text span fw={650} fz={14} ff="monospace">{dateTimeSecFormat(order?.createdAt)}</Text>
        </Text>
        <Text fz={14}>
          {t("supplierSales:sales.salesNumber")}: <Text span fw={650} fz={14} ff="monospace">{order?.salesCode || "-"}</Text>
        </Text>
      </Group>

      <Grid gap="md" align="stretch">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Box className={classes.card} data-help-id="cust-box">
            <Title order={3} className={classes.cardTitle}>{t("supplierSales:sales.customerInfo")}<HelpIcon helpKey="order.supplier-sales-new.section.customer" variant="section" /></Title>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <TextInput
                  {...form.getInputProps("regNumber")}
                  label={<>{t("supplierSales:sales.searchByTaxId")}<HelpIcon helpKey="order.supplier-sales-new.field.customer-search" /></>}
                  placeholder={t("supplierSales:sales.taxIdPlaceholder")}
                  onChange={(e) => {
                    form.setFieldValue("regNumber", e.currentTarget.value)
                    form.setFieldValue("partnerName", undefined as any)
                    form.setFieldValue("supplierId", undefined as any)
                  }}
                  rightSection={<MagnifyingGlassIcon size={16} />}
                  required
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <TextInput
                  {...form.getInputProps("partnerName")}
                  label={t("supplierSales:sales.searchByPartnerName")}
                  placeholder={t("supplierSales:sales.searchByNamePlaceholder")}
                  onChange={(e) => {
                    form.setFieldValue("partnerName", e.currentTarget.value)
                    form.setFieldValue("regNumber", undefined as any)
                    form.setFieldValue("supplierId", undefined as any)
                  }}
                  rightSection={
                    <UnstyledButton
                      aria-label={t("common:actions.clear")}
                      onClick={() => {
                        form.setFieldValue("partnerName", undefined as any)
                        form.setFieldValue("supplierId", undefined as any)
                        form.setFieldValue("regNumber", undefined as any)
                      }}>
                      <XIcon size={16} />
                    </UnstyledButton>
                  }
                  required
                />
              </Grid.Col>
              {(ORD_SAVE.hasAccess || ORD_SEND.hasAccess) && (
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <Select
                    {...form.getInputProps("supplierId")}
                    label={t("supplierSales:sales.buyerBusiness")}
                    placeholder={t("common:actions.select")}
                    data={(networkData?.res || [])
                      .filter((a: any) => {
                        if (form.values.regNumber) return a.regNumber === form.values.regNumber
                        if (form.values.partnerName) return a.partnerName.includes(form.values.partnerName)
                        return true
                      })
                      .map((item: IBusiness) => ({ label: renderBusinessName(item, ""), value: item.id }))}
                    onChange={(e) => {
                      form.setFieldValue("regNumber", networkData?.mapedRes[e!]?.regNumber)
                      form.setFieldValue("partnerName", networkData?.mapedRes[e!]?.partnerName)
                      form.setFieldValue("supplierId", e as any)
                      businessOnChange(e)
                    }}
                    required
                  />
                </Grid.Col>
              )}
            </Grid>

            <Box className={classes.divider} />

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField label={t("supplierSales:sales.partnerName")} value={refAndName(info.partner, true)} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField label={t("supplierSales:sales.customerName")} value={refAndName(info.business)} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField label={t("supplierSales:sales.taxId")} value={info.business?.regNumber} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField label={<>{t("supplierSales:sales.paymentTerm")}<HelpIcon helpKey="order.supplier-sales-new.field.payment-term" /></>} value={info.paymentTerm?.description} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField label={t("supplierSales:sales.supplierManager")} value={renderName(info.supplierStaff)} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField label={t("supplierSales:sales.buyerManager")} value={renderName(info.buyerStaff)} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField label={t("supplierSales:sales.receiverStaff")} value={renderName(info.receiverStaff)} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField label={t("supplierSales:sales.receiverBranch")} value={info.receiverBranch?.name} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                <LabelField
                  label={t("supplierSales:sales.receiverAddress")}
                  icon={<MapPinIcon size={16} />}
                  value={info.receiverBranch?.branchAddress}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                {/* The backend does not store whether the customer is a VAT payer: shown as «-», never «N/A». */}
                <LabelField label={t("supplierSales:sales.vatPayer")} value="-" />
              </Grid.Col>
            </Grid>
          </Box>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Box className={classes.card} data-help-id="days">
            <Title order={3} className={classes.cardTitle}>{t("supplierSales:sales.deliverySection")}<HelpIcon helpKey="order.supplier-sales-new.section.delivery" variant="section" /><HelpIcon helpKey="order.supplier-sales-new.field.delivery-type" /></Title>
            <Stack gap="md">
              <SegmentedControl
                fullWidth
                value={form.values.deliveryType || ""}
                onChange={(v) => form.setFieldValue("deliveryType", v)}
                data={(deliveryDateTypes || []).map((item: any) => ({ value: item.code, label: item.name }))}
              />
              {form.errors?.deliveryType && <Text fz={14} c="gray.8">{form.errors.deliveryType}</Text>}
              {form.values?.deliveryType === "DEFAULT_DATE" ? (
                <Input.Wrapper label={<>{t("supplierSales:sales.nearestDeliveryDate")}<HelpIcon helpKey="order.supplier-sales-new.field.schedule-day" /></>} error={!form.values?.deliveryCheck && form.errors?.deliveryCheck}>
                  <Grid mt="xs">
                    {dayChips.map((date) => (
                      <Grid.Col span={4} key={date.toISOString()}>
                        <UnstyledButton
                          className={classes.dayChip}
                          data-active={(selectedChip && sameDay(selectedChip, date)) || undefined}
                          aria-pressed={!!selectedChip && sameDay(selectedChip, date)}
                          onClick={() => form.setFieldValue("deliveryCheck", date.toISOString())}>
                          <Text fz={14} fw={600} ff="monospace">{dateFormat(date)}</Text>
                          <Text fz={14} className={classes.dayWeekday}>{dayLabels[date.getDay()]}</Text>
                        </UnstyledButton>
                      </Grid.Col>
                    ))}
                  </Grid>
                </Input.Wrapper>
              ) : (
                <DatePickerInput
                  {...form.getInputProps("deliveryDate")}
                  value={form.values.deliveryDate ? new Date(form.values.deliveryDate) : null}
                  onChange={(v) => form.setFieldValue("deliveryDate", v ? dayjs(v).format() : "")}
                  placeholder={t("supplierSales:sales.selectDate")}
                  label={t("supplierSales:sales.nearestDeliveryDate")}
                  required
                />
              )}
            </Stack>
          </Box>
        </Grid.Col>
      </Grid>

      <SupplierSalesDynamicForm values={form.values} setFieldValue={form.setFieldValue} errors={form.errors} />
    </PageLayout>
  )
}

const useBreadcrumb = () => {
  const { t } = useTranslation(["supplierSales", "common"])
  return [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/supplier-sales", label: t("supplierSales:sales.title") },
    { label: t("supplierSales:sales.editOrder") },
  ]
}
